/**
 * DocumentUploadTab
 * =================
 *
 * The "Document" tab inside the study's Upload dialog (Phase 1 of the
 * document-unification work). It's a self-contained drop-zone +
 * uploader for non-conversational files (PDF/DOCX/TXT — blog, article,
 * report) that the user wants stored as a `Qualitative_Items` row with
 * `item_type="document"`.
 *
 * Why a separate component (rather than threading a fourth case through
 * `FileUpload.jsx`'s tab logic): the existing tabs (Text/Audio/Video)
 * share a complex pipeline (pre-signed URL, block upload, diarisation,
 * estimation). Documents don't need any of that — one direct multipart
 * POST to the unified endpoint, paragraphs extracted server-side,
 * `Qualitative_Items` row written. Isolating the new path here keeps
 * the existing transcript flow byte-for-byte unchanged and the diff
 * targeted.
 *
 * On success the document appears in the existing transcripts tab of
 * the study (same collection, same list endpoint), so the user can view
 * its content immediately without running an analysis.
 *
 * Props:
 *   - studyId  string   (required) — study to attach the document to
 *   - onClose  ()=>void (optional) — called once all uploads complete
 */

import { Box, Button, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useUploadDocumentAsQualitativeItem } from '@hooks/postQueries';
import { DEFAULT_REDACTION_POLICIES } from '@constants/transcriptWorkflows';
import { showErrorToast, showSuccessToast } from '@utils/toast';
import { AlertTriangle, CheckCircle2, FileText, Upload, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { PiiRedactionControls } from './PiiRedactionControls';

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
};

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB — matches the backend validator
const MAX_FILES_PER_BATCH = 10;

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** One staged file's progress state — uploading | done | error */
const newFileState = (file) => ({
  file,
  id: `${file.name}-${file.size}-${file.lastModified}`,
  status: 'pending',
  percent: 0,
  error: null,
  result: null,
});

const DocumentUploadTab = ({ studyId, onClose }) => {
  const theme = useTheme();
  const uploadMutation = useUploadDocumentAsQualitativeItem();
  const [files, setFiles] = useState([]);

  // PII redaction state — mirrors ConfigureDialog's transcript flow.
  // Stays at the tab level so all files in one drop share the same
  // redaction settings (which is what users almost always want).
  const [redactPii, setRedactPii] = useState(false);
  const [redactionSubstitution, setRedactionSubstitution] = useState('entity_name');
  const [redactionPolicies, setRedactionPolicies] = useState([...DEFAULT_REDACTION_POLICIES]);

  const togglePolicy = (policyValue) => {
    setRedactionPolicies((prev) =>
      prev.includes(policyValue) ? prev.filter((p) => p !== policyValue) : [...prev, policyValue],
    );
  };

  const updateFile = (id, patch) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const uploadOne = useCallback(
    async (entry) => {
      updateFile(entry.id, { status: 'uploading', percent: 0 });
      try {
        const result = await uploadMutation.mutateAsync({
          studyId,
          file: entry.file,
          // Redaction is per-batch (tab state, captured in closure at
          // drop time). Sending these only when toggled on keeps the
          // wire payload clean and the default path byte-identical to
          // pre-redaction behaviour.
          redactPii,
          redactionSubstitution,
          redactionPolicies,
          onUploadProgress: (percent) => updateFile(entry.id, { percent }),
        });
        updateFile(entry.id, { status: 'done', percent: 100, result });
        if (result?.warning) {
          // Image-only / blank documents upload successfully but the
          // extractor found nothing. Row styling picks up the warning
          // separately; toast tells the customer at a glance.
          showSuccessToast(`"${entry.file.name}" saved — no readable text was found in this file.`);
        } else {
          showSuccessToast(
            `Uploaded "${entry.file.name}" — ${result?.paragraph_count ?? 0} paragraphs extracted${
              result?.redaction_applied ? ' (PII redacted)' : ''
            }.`,
          );
        }
        return { ok: true };
      } catch (err) {
        // Backend's 422 detail is customer-facing (e.g. "This document
        // doesn't contain any readable text. If it's a scanned PDF,
        // try one with selectable text instead."). We want to surface
        // that on the file row.
        //
        // Shape gotcha: utils/axios.js has a global response interceptor
        // that rejects with `{...response.data, status}` instead of the
        // stock axios error, so err.detail is populated — err.response
        // is undefined. Handle both shapes so this stays robust if the
        // interceptor ever changes back, and cover Pydantic's list-shaped
        // validation-error detail while we're at it.
        const extractDetail = (e) => {
          const candidates = [e?.detail, e?.response?.data?.detail];
          for (const value of candidates) {
            if (typeof value === 'string' && value.trim()) return value;
            if (Array.isArray(value) && value.length) {
              return value.map((v) => v?.msg || String(v)).join('; ');
            }
          }
          return e?.message || 'Upload failed.';
        };
        const detail = extractDetail(err);
        updateFile(entry.id, { status: 'error', percent: 0, error: detail });
        return { ok: false, detail };
      }
    },
    [studyId, uploadMutation, redactPii, redactionSubstitution, redactionPolicies],
  );

  // Drop = STAGE files only. Uploads don't start until the user clicks
  // "Upload". This is the fix for the previous footgun where dropping
  // started the request immediately, so flipping the PII toggle after
  // dropping had no effect — the upload had already happened without it.
  const onDrop = useCallback(
    (accepted) => {
      if (!accepted?.length) return;

      const remaining = MAX_FILES_PER_BATCH - files.length;
      if (accepted.length > remaining) {
        showErrorToast(
          `You can upload up to ${MAX_FILES_PER_BATCH} documents at a time. ` +
            `Submit or remove a few, then add more.`,
        );
        return;
      }

      const oversize = accepted.find((f) => f.size > MAX_BYTES);
      if (oversize) {
        showErrorToast(
          `"${oversize.name}" is larger than 50 MB. Smaller documents only — ` +
            `image-heavy PDFs over 50 MB rarely contain selectable text anyway.`,
        );
        return;
      }

      // Skip files already staged (same name+size+lastModified). Stops
      // duplicate-drag from creating ghost entries that fail to upload.
      const existingIds = new Set(files.map((f) => f.id));
      const staged = accepted.map(newFileState).filter((entry) => !existingIds.has(entry.id));

      if (staged.length === 0) {
        showErrorToast('Those files are already staged. Click Upload to send them.');
        return;
      }

      setFiles((prev) => [...prev, ...staged]);
    },
    [files],
  );

  // Explicit "Upload" trigger — captures the current PII redaction
  // settings at click time (not at drop time) so users can configure
  // after dropping. Uploads sequentially so per-file progress is easy
  // to read and the user can tell which one failed if any does.
  const handleStartUpload = useCallback(async () => {
    const pending = files.filter((f) => f.status === 'pending' || f.status === 'error');
    if (pending.length === 0) return;

    for (const entry of pending) {
      await uploadOne(entry);
    }
  }, [files, uploadOne]);

  const onReject = useCallback((rejections) => {
    const first = rejections?.[0]?.errors?.[0]?.code;
    if (first === 'file-invalid-type') {
      showErrorToast(
        'Only PDF, DOCX, or TXT documents are accepted on this tab. For ' +
          'interviews, use the Text / Audio / Video tabs instead.',
      );
    } else if (first === 'file-too-large') {
      showErrorToast('Documents must be 50 MB or smaller.');
    } else {
      showErrorToast('That file was rejected — please try a different one.');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    maxSize: MAX_BYTES,
    multiple: true,
    onDrop,
    onDropRejected: onReject,
  });

  const isBusy = files.some((f) => f.status === 'uploading');
  const hasAny = files.length > 0;
  const pendingCount = files.filter((f) => f.status === 'pending' || f.status === 'error').length;
  const allDone = hasAny && files.every((f) => f.status === 'done');

  const handleRemove = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      {/* PII redaction — placed BEFORE the dropzone so users see the
          toggle first and can configure it before staging any files.
          Settings apply to every upload kicked off by the Upload
          button below; captured at click time, not drop time. */}
      <PiiRedactionControls
        redactPii={redactPii}
        onRedactPiiChange={setRedactPii}
        redactionSubstitution={redactionSubstitution}
        onSubstitutionChange={setRedactionSubstitution}
        redactionPolicies={redactionPolicies}
        onTogglePolicy={togglePolicy}
        disabled={isBusy}
        description="Automatically remove personal information from each paragraph during upload."
        variant="card"
      />

      {/* Drop zone — moved below the redaction settings on purpose, so
          users configure first and aren't surprised by uploads kicking
          off the moment they drop a file. */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 1,
          minHeight: { xs: 160, sm: 180 },
          textAlign: 'center',
          bgcolor: 'grey.50',
          cursor: isBusy ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <input {...getInputProps()} disabled={isBusy} />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2 }}>
          <Upload size={20} color={theme.palette.primary.main} />
          <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
            Drag and drop or click to add documents
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B6B6B', mt: 0.5 }}>
            Supports PDF, DOCX, and TXT — non-conversational files (blogs, articles, reports).
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B6B6B' }}>
            Up to 50 MB per file. Files are staged here; click <strong>Upload</strong> below to send
            them.
          </Typography>
        </Box>
      </Box>

      {/* Staged file list with per-file progress */}
      {hasAny && (
        <Stack spacing={1} sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {files.map((f) => {
            // Warning state: upload succeeded but the extractor found
            // no readable text (image-only PDF etc). Row styled amber,
            // not red — the file is safely saved with the study, we
            // just want to tell the customer analysis will skip it.
            const hasWarning = f.status === 'done' && !!f.result?.warning;
            const rowBg = f.status === 'error' ? '#FFF5F5' : hasWarning ? '#FFF9EB' : 'grey.50';
            const rowBorder =
              f.status === 'error' ? '#FECACA' : hasWarning ? '#FDE68A' : 'transparent';
            const fileIconColor =
              f.status === 'done' && !hasWarning
                ? theme.palette.success.main
                : hasWarning
                  ? theme.palette.warning.main
                  : theme.palette.text.secondary;
            return (
              <Box
                key={f.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: rowBg,
                  border: '1px solid',
                  borderColor: rowBorder,
                }}
              >
                <FileText size={18} color={fileIconColor} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {f.file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: hasWarning ? '#92400E' : '#6B6B6B' }}>
                    {formatBytes(f.file.size)}
                    {f.status === 'uploading' && ` · uploading ${f.percent}%`}
                    {f.status === 'done' &&
                      !hasWarning &&
                      f.result?.paragraph_count !== undefined &&
                      ` · ${f.result.paragraph_count} paragraphs`}
                    {hasWarning && ` · ${f.result.warning}`}
                    {f.status === 'error' && ` · ${f.error}`}
                  </Typography>
                  {f.status === 'uploading' && (
                    <LinearProgress
                      variant="determinate"
                      value={f.percent}
                      sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                    />
                  )}
                </Box>
                {f.status === 'done' && !hasWarning && (
                  <CheckCircle2 size={18} color={theme.palette.success.main} />
                )}
                {hasWarning && <AlertTriangle size={18} color={theme.palette.warning.main} />}
                {f.status !== 'uploading' && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(f.id)}
                    aria-label="Remove file"
                  >
                    <X size={16} />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="text" onClick={onClose} disabled={isBusy}>
          {allDone ? 'Done' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleStartUpload}
          disabled={isBusy || pendingCount === 0}
        >
          {isBusy
            ? 'Uploading…'
            : pendingCount === 0
              ? 'Upload'
              : `Upload ${pendingCount} ${pendingCount === 1 ? 'file' : 'files'}`}
          {redactPii && pendingCount > 0 && !isBusy ? ' (with PII redaction)' : ''}
        </Button>
      </Box>
    </Box>
  );
};

export default DocumentUploadTab;
